import { useParams, useNavigate } from 'react-router-dom'
import { useEffect } from 'react'
import PageContainer from '@/common/components/PageContainer'
import withUserPermission from '@/common/components/WithUserPermission'
import { PermissionType } from '@/common/types'
import { PermissionKeys } from '@/modules/manage-member/mappers'
import { Button } from '@/common/ui/button'
import { ArrowLeft } from 'lucide-react'
import { Card, CardContent } from '@/common/ui/card'
import BrokeFlexPresetConfigForm from '../../components/BrokeFlexPresetConfigForm'
import BrokeFlexMessageCampaignForm from '../../components/BrokeFlexMessageCampaignForm'
import useQueryGetBrokeFlexMessageCampaign from '../../api/hooks/useQueryGetBrokeFlexMessageCampaign'
import useQueryGetBrokeFlexPresets from '../../api/hooks/useQueryGetBrokeFlexPresets'

const EditBrokeFlexMessagePage = () => {
	const { id } = useParams<{ id: string }>()
	const navigate = useNavigate()

	const {
		data: campaign,
		isLoading: campaignLoading,
		error,
	} = useQueryGetBrokeFlexMessageCampaign(id || '')

	const { data: presetsData, isLoading: presetsLoading } =
		useQueryGetBrokeFlexPresets()

	useEffect(() => {
		if (!id) {
			navigate('/operator/messaging/broke-flex-message')
		}
	}, [id, navigate])

	useEffect(() => {
		if (error) {
			navigate('/operator/messaging/broke-flex-message')
		}
	}, [error, navigate])

	const handleSuccess = () => {
		navigate('/operator/messaging/broke-flex-message')
	}

	const handleBack = () => {
		navigate('/operator/messaging/broke-flex-message')
	}

	if (!id) return null

	const isLoading = campaignLoading || presetsLoading

	if (isLoading) {
		return (
			<PageContainer>
				<div className="space-y-6">
					<div className="flex items-center gap-4">
						<Button variant="outline" size="sm" onClick={handleBack}>
							<ArrowLeft className="mr-2 h-4 w-4" />
							กลับ
						</Button>
						<h1 className="text-3xl font-bold">
							แก้ไขแคมเปญ Broke Flex Message
						</h1>
					</div>
					<Card>
						<CardContent className="p-6">
							<div className="flex h-32 items-center justify-center">
								<div className="text-muted-foreground">กำลังโหลด...</div>
							</div>
						</CardContent>
					</Card>
				</div>
			</PageContainer>
		)
	}

	if (!campaign) {
		return (
			<PageContainer>
				<div className="flex h-64 items-center justify-center">
					<p className="text-muted-foreground">ไม่พบข้อมูลแคมเปญ</p>
				</div>
			</PageContainer>
		)
	}

	// Detect if campaign is custom (has "(C)" suffix) or preset
	const isCustomCampaign = campaign.name?.includes('(C)')

	// Find matching preset based on campaign data (for preset campaigns)
	const preset = presetsData?.data[0] // Default to first preset for now

	return (
		<PageContainer>
			<div className="space-y-6">
				<div className="flex items-center gap-4">
					<Button variant="outline" size="sm" onClick={handleBack}>
						<ArrowLeft className="mr-2 h-4 w-4" />
						กลับ
					</Button>
					<div>
						<h1 className="text-3xl font-bold">
							แก้ไขแคมเปญ Broke Flex Message
						</h1>
						<p className="mt-2 text-muted-foreground">
							{isCustomCampaign
								? 'แก้ไขแคมเปญแบบกำหนดเอง (ควบคุมเต็มรูปแบบ)'
								: 'แก้ไขข้อความและโปรโมชั่นในแคมเปญ'}
						</p>
					</div>
				</div>

				{isCustomCampaign ? (
					<BrokeFlexMessageCampaignForm
						mode="edit"
						campaignId={id}
						initialData={campaign}
						onSuccess={handleSuccess}
					/>
				) : (
					preset && (
						<BrokeFlexPresetConfigForm
							preset={preset}
							onSuccess={handleSuccess}
							onCancel={handleBack}
							editMode={true}
							campaignId={id}
							initialData={campaign}
						/>
					)
				)}
			</div>
		</PageContainer>
	)
}

const GuardedEditBrokeFlexMessagePage = withUserPermission(
	EditBrokeFlexMessagePage,
	{
		permissionType: PermissionType.Write,
		permissionKey: [PermissionKeys.MANAGE_SMS],
	},
)

export { GuardedEditBrokeFlexMessagePage as EditBrokeFlexMessagePage }
